import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Play, Square, Settings2, Wind, History, RotateCcw, Pencil, Trash2, GitMerge, ShieldAlert } from "lucide-react";
import { fmtLocalDT } from "@/lib/clinical";
import { useAuth } from "@/lib/auth";

// Convert ISO string to <input type="datetime-local"> compatible value (local time, no seconds).
function toLocalDTInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// Convert datetime-local input value → ISO string (UTC).
function fromLocalDTInput(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

const FIELD_STEP = { pip: 0.5, peep: 0.5, rate: 1, fio2: 1, itime: 0.01, map: 0.5, flow_lpm: 0.5, amplitude: 0.5, frequency_hz: 0.5 };
const INTERFACE_OPTIONS = ["Short binasal prongs", "RAM cannula", "Nasal mask", "Full face mask", "ETT", "Tracheostomy"];
const METHOD_OPTIONS = ["Nasal cannula", "Hood", "Face mask", "Blow-by"];

function fmtDur(from, to) {
  const f = new Date(from); const t = to ? new Date(to) : new Date();
  const ms = Math.max(0, t - f);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 24) { const d = Math.floor(h / 24); return `${d}d ${h % 24}h`; }
  return h ? `${h}h ${m}m` : `${m}m`;
}

const MODE_TONE = {
  VENT_SIMV: "bg-critical", VENT_AC: "bg-critical", VENT_PSV: "bg-critical", VENT_HFOV: "bg-critical", VENT_NIV: "bg-critical",
  CPAP: "bg-warn", HFNC: "bg-warn", LF_O2: "bg-photo", ROOM_AIR: "bg-vacant",
};

export default function RespiratoryTab({ patient }) {
  const { user } = useAuth();
  const canEdit = ["admin", "doctor"].includes(user?.role);
  const [modes, setModes] = useState(null);
  const [active, setActive] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [startOpen, setStartOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [editSession, setEditSession] = useState(null);   // session doc when editing
  const [mergeSession, setMergeSession] = useState(null); // source session when merging
  const [deleteSession, setDeleteSession] = useState(null);

  const [form, setForm] = useState({ mode: "CPAP", settings: {}, notes: "" });

  const load = useCallback(async () => {
    try {
      const [{ data: m }, { data: a }, { data: s }] = await Promise.all([
        api.get("/respiratory/modes"),
        api.get(`/respiratory/${patient.id}/active`),
        api.get(`/respiratory/${patient.id}/sessions`),
      ]);
      setModes(m);
      setActive(a);
      setSessions(s || []);
    } catch (e) { console.warn("Respiratory load failed", e); }
  }, [patient.id]);
  useEffect(() => { load(); }, [load]);

  const modeMeta = useMemo(() => {
    if (!modes) return null;
    return modes.modes.reduce((acc, m) => { acc[m.id] = m; return acc; }, {});
  }, [modes]);

  const openStart = (cloneFrom = null) => {
    if (cloneFrom) {
      setForm({ mode: cloneFrom.mode, settings: { ...(cloneFrom.current_settings || {}) }, notes: "" });
    } else {
      setForm({ mode: "CPAP", settings: {}, notes: "" });
    }
    setStartOpen(true);
  };

  const openChange = () => {
    if (!active) return;
    setForm({ mode: active.mode, settings: { ...(active.current_settings || {}) }, notes: "" });
    setChangeOpen(true);
  };

  const submitStart = async () => {
    try {
      await api.post(`/respiratory/${patient.id}/start`, form);
      toast.success(`Started ${modeMeta?.[form.mode]?.label || form.mode}`);
      setStartOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); }
  };

  const submitChange = async () => {
    try {
      await api.post(`/respiratory/${patient.id}/change`, form);
      toast.success("Settings updated");
      setChangeOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitEnd = async (notes) => {
    try {
      await api.post(`/respiratory/${patient.id}/end`, { notes });
      toast.success("Session ended");
      setEndOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const renderFields = (mode, settings, onChange) => {
    if (!modeMeta) return null;
    const meta = modeMeta[mode];
    if (!meta || meta.fields.length === 0) return <div className="text-xs text-muted-foreground">No settings for this mode.</div>;
    const labels = modes.field_labels;
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {meta.fields.map((f) => (
          <div key={f} className="space-y-1">
            <label className="text-[11px] text-muted-foreground">{labels[f] || f}</label>
            {f === "interface" ? (
              <Select value={settings[f] || ""} onValueChange={(v) => onChange({ ...settings, [f]: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{INTERFACE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            ) : f === "method" ? (
              <Select value={settings[f] || ""} onValueChange={(v) => onChange({ ...settings, [f]: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{METHOD_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input type="number" step={FIELD_STEP[f] || 0.1}
                     value={settings[f] ?? ""}
                     onChange={(e) => onChange({ ...settings, [f]: e.target.value === "" ? "" : Number(e.target.value) })}
                     data-testid={`resp-${f}`} />
            )}
          </div>
        ))}
      </div>
    );
  };

  const activeSettings = active?.current_settings || {};
  const activeMeta = active ? modeMeta?.[active.mode] : null;
  const labels = modes?.field_labels || {};

  return (
    <div className="space-y-6" data-testid="respiratory-tab">
      {/* Active session card */}
      {active ? (
        <div className="card-clinical p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] ${MODE_TONE[active.mode] || "bg-muted"}`}>
                  <Wind size={12} /> {activeMeta?.label || active.mode}
                </span>
                <Badge variant="secondary" className="rounded-full text-[10px]">running {fmtDur(active.started_at)}</Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Started {fmtLocalDT(active.started_at)} by {active.started_by}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full" onClick={openChange} data-testid="resp-change"><Settings2 size={14} className="mr-2" /> Change settings</Button>
              <Button variant="destructive" className="rounded-full" onClick={() => setEndOpen(true)} data-testid="resp-end"><Square size={14} className="mr-2" /> End</Button>
            </div>
          </div>

          {/* Current settings pill grid */}
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {activeMeta?.fields?.length ? activeMeta.fields.map((f) => (
              <div key={f} className="rounded-xl bg-muted/40 px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{labels[f] || f}</div>
                <div className="mt-1 font-display font-semibold tabular text-xl">{activeSettings[f] ?? "—"}</div>
              </div>
            )) : (
              <div className="col-span-full text-sm text-muted-foreground">No settings required for this mode.</div>
            )}
          </div>

          {/* Snapshots (current session change log) */}
          {active.snapshots?.length > 1 && (
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2"><History size={11} /> Change log</div>
              <div className="max-h-40 overflow-auto pr-1 space-y-1 text-xs">
                {[...active.snapshots].reverse().map((s) => (
                  <div key={s.id} className="flex items-start gap-2 py-1 border-b border-border/60">
                    <span className="tabular text-muted-foreground w-32 shrink-0">{fmtLocalDT(s.at)}</span>
                    <span className="capitalize text-primary w-20 shrink-0">{s.kind}</span>
                    <span className="text-muted-foreground truncate">
                      {Object.entries(s.settings || {}).map(([k, v]) => `${k}=${v}`).join(" · ") || "—"}
                      {s.notes ? ` · ${s.notes}` : ""}
                    </span>
                    <span className="text-muted-foreground ml-auto shrink-0">{s.by}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card-clinical p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex-1">
            <div className="text-sm text-muted-foreground">Currently on <span className="font-semibold text-foreground">Room air</span> — no active respiratory support.</div>
            <div className="text-xs text-muted-foreground mt-1">Start CPAP / Ventilator / HFNC / Low-flow O₂ when needed. You can end and restart at any time.</div>
          </div>
          <Button className="rounded-full" onClick={() => openStart()} data-testid="resp-start"><Play size={14} className="mr-2" /> Start respiratory support</Button>
        </div>
      )}

      {/* Sessions history */}
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2"><History size={11} /> Session history</div>
        <div className="card-clinical overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-2 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
            <div className="col-span-3">Mode</div>
            <div className="col-span-3">Started</div>
            <div className="col-span-3">Ended</div>
            <div className="col-span-2">Duration</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {sessions.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No respiratory sessions yet</div>}
          {sessions.map((s) => (
            <div key={s.id} className="grid grid-cols-12 px-4 py-3 border-b border-border/60 items-center text-sm" data-testid={`resp-session-${s.id}`}>
              <div className="col-span-3">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${MODE_TONE[s.mode] || "bg-muted"}`}>
                  <Wind size={11} /> {modeMeta?.[s.mode]?.label || s.mode}
                </span>
                {s.active && <span className="ml-2 text-[10px] text-emerald-600 dark:text-emerald-400">● active</span>}
              </div>
              <div className="col-span-3 tabular text-xs">{fmtLocalDT(s.started_at)}</div>
              <div className="col-span-3 tabular text-xs">{s.ended_at ? fmtLocalDT(s.ended_at) : "—"}</div>
              <div className="col-span-2 tabular">{fmtDur(s.started_at, s.ended_at)}</div>
              <div className="col-span-1 flex items-center justify-end gap-1">
                {!s.active && (
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-full" onClick={() => openStart(s)} title="Restart with same settings">
                    <RotateCcw size={12} />
                  </Button>
                )}
                {canEdit && (
                  <>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-full text-muted-foreground hover:text-primary"
                            onClick={() => setEditSession(s)} title="Edit session" data-testid={`edit-session-${s.id}`}>
                      <Pencil size={12} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-full text-muted-foreground hover:text-amber-500"
                            onClick={() => setMergeSession(s)} title="Merge into another session" data-testid={`merge-session-${s.id}`}>
                      <GitMerge size={12} />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-full text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteSession(s)} title="Delete session" data-testid={`delete-session-${s.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Start dialog */}
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Start respiratory support</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-muted-foreground">Mode</label>
              <Select value={form.mode} onValueChange={(v) => setForm((s) => ({ ...s, mode: v, settings: {} }))}>
                <SelectTrigger data-testid="resp-start-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {modes?.modes?.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {renderFields(form.mode, form.settings, (s) => setForm((f) => ({ ...f, settings: s })))}
            <div>
              <label className="text-[11px] text-muted-foreground">Notes</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} placeholder="Reason / clinical context (optional)" />
            </div>
            {active && (
              <div className="text-xs bg-warn rounded-md px-3 py-2">
                Note — this will automatically end the current {modeMeta?.[active.mode]?.label} session.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStartOpen(false)}>Cancel</Button>
            <Button className="rounded-full" onClick={submitStart} data-testid="resp-start-submit">Start</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change dialog */}
      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Change settings — {modeMeta?.[form.mode]?.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-muted-foreground">Mode (change to switch e.g. CPAP → HFNC)</label>
              <Select value={form.mode} onValueChange={(v) => setForm((s) => ({ ...s, mode: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {modes?.modes?.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {renderFields(form.mode, form.settings, (s) => setForm((f) => ({ ...f, settings: s })))}
            <div>
              <label className="text-[11px] text-muted-foreground">Reason for change</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setChangeOpen(false)}>Cancel</Button>
            <Button className="rounded-full" onClick={submitChange} data-testid="resp-change-submit">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End dialog */}
      <Dialog open={endOpen} onOpenChange={setEndOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>End respiratory session</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Baby will move to room air. You can restart at any time.</p>
          <Textarea id="endnote" rows={2} placeholder="Reason (extubation, wean, transferred, etc.)" onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEndOpen(false)}>Cancel</Button>
            <Button variant="destructive" className="rounded-full" onClick={() => submitEnd(form.notes)} data-testid="resp-end-submit">End session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editSession && (
        <EditSessionModal
          session={editSession}
          modes={modes}
          modeMeta={modeMeta}
          onClose={() => setEditSession(null)}
          onSaved={() => { setEditSession(null); load(); }}
        />
      )}
      {mergeSession && (
        <MergeSessionModal
          source={mergeSession}
          sessions={sessions}
          modeMeta={modeMeta}
          onClose={() => setMergeSession(null)}
          onSaved={() => { setMergeSession(null); load(); }}
        />
      )}
      {deleteSession && (
        <DeleteSessionModal
          session={deleteSession}
          modeMeta={modeMeta}
          onClose={() => setDeleteSession(null)}
          onDeleted={() => { setDeleteSession(null); load(); }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------- */
/*  Session editor components — admin / doctor only               */
/* ------------------------------------------------------------- */
function EditSessionModal({ session, modes, modeMeta, onClose, onSaved }) {
  const [mode, setMode] = useState(session.mode);
  const [startedAt, setStartedAt] = useState(toLocalDTInput(session.started_at));
  const [endedAt, setEndedAt] = useState(toLocalDTInput(session.ended_at));
  const [reactivate, setReactivate] = useState(false);
  const [settingsJson, setSettingsJson] = useState(JSON.stringify(session.current_settings || {}, null, 2));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    let parsedSettings = null;
    try {
      parsedSettings = JSON.parse(settingsJson || "{}");
    } catch {
      return toast.error("Settings must be valid JSON");
    }
    const payload = {
      mode,
      started_at: fromLocalDTInput(startedAt),
      ended_at: reactivate ? "" : (endedAt ? fromLocalDTInput(endedAt) : null),
      settings: parsedSettings,
      notes: notes || `Retroactive edit by ${new Date().toLocaleString()}`,
    };
    setSaving(true);
    try {
      await api.patch(`/respiratory/session/${session.id}`, payload);
      toast.success("Session updated · bill re-accrued");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update session");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="edit-session-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil size={16} /> Edit respiratory session</DialogTitle>
          <DialogDescription>
            Retroactive documentation correction. Changes are audit-logged and equipment charges will be automatically re-accrued.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] text-muted-foreground">Mode</label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="edit-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {modes?.modes?.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-muted-foreground">Started at</label>
              <Input type="datetime-local" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} data-testid="edit-started-at" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Ended at {reactivate && <span className="text-emerald-500">(will re-activate)</span>}</label>
              <Input type="datetime-local" value={endedAt} disabled={reactivate}
                     onChange={(e) => setEndedAt(e.target.value)} data-testid="edit-ended-at" />
            </div>
          </div>
          {!session.active && (
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={reactivate} onChange={(e) => setReactivate(e.target.checked)} />
              Re-activate this session (will auto-end any other active session)
            </label>
          )}
          <div>
            <label className="text-[11px] text-muted-foreground">Current settings (JSON)</label>
            <Textarea rows={4} value={settingsJson} onChange={(e) => setSettingsJson(e.target.value)}
                      className="font-mono text-xs" data-testid="edit-settings-json" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">Reason for edit (audit)</label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="e.g. wrong start time entered by nurse; corrected from paper chart"
                      data-testid="edit-notes" />
          </div>
          <div className="text-xs bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2 flex items-start gap-2">
            <ShieldAlert size={12} className="mt-0.5 text-amber-600 shrink-0" />
            <span>Edits change past-day billing. Existing invoiced (finalized) bills are unaffected.</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-session-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MergeSessionModal({ source, sessions, modeMeta, onClose, onSaved }) {
  const candidates = useMemo(
    () => sessions.filter((s) => s.id !== source.id && s.mode === source.mode),
    [sessions, source]
  );
  const [targetId, setTargetId] = useState(candidates[0]?.id || "");
  const [saving, setSaving] = useState(false);
  const target = candidates.find((s) => s.id === targetId);

  const submit = async () => {
    if (!targetId) return toast.error("Pick a target session");
    setSaving(true);
    try {
      await api.post(`/respiratory/session/${source.id}/merge?into=${targetId}`);
      toast.success("Sessions merged · bill re-accrued");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="merge-session-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitMerge size={16} /> Merge respiratory sessions</DialogTitle>
          <DialogDescription>
            Merge only allowed for same-mode sessions. Source will be deleted; target absorbs the earliest start → latest end range.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-border p-3 bg-muted/30">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Source (will be deleted)</div>
            <div className="mt-1">{modeMeta?.[source.mode]?.label || source.mode} · {fmtLocalDT(source.started_at)} → {source.ended_at ? fmtLocalDT(source.ended_at) : "active"}</div>
          </div>
          {candidates.length === 0 ? (
            <div className="text-xs text-muted-foreground">No other same-mode sessions available to merge into.</div>
          ) : (
            <div>
              <label className="text-[11px] text-muted-foreground">Merge into</label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger data-testid="merge-target"><SelectValue placeholder="Pick target session" /></SelectTrigger>
                <SelectContent>
                  {candidates.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {fmtLocalDT(s.started_at)} → {s.ended_at ? fmtLocalDT(s.ended_at) : "active"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {target && (
                <div className="mt-2 text-xs text-muted-foreground">
                  Merged range: {fmtLocalDT([source.started_at, target.started_at].sort()[0])} → {" "}
                  {(source.active || target.active) ? "active" :
                    fmtLocalDT([source.ended_at, target.ended_at].filter(Boolean).sort().slice(-1)[0])}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !targetId || candidates.length === 0} data-testid="merge-session-save">
            {saving ? "Merging…" : "Merge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteSessionModal({ session, modeMeta, onClose, onDeleted }) {
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.delete(`/respiratory/session/${session.id}`);
      toast.success("Session deleted · bill re-accrued");
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="delete-session-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive"><Trash2 size={16} /> Delete respiratory session</DialogTitle>
          <DialogDescription>
            This will permanently remove the {modeMeta?.[session.mode]?.label || session.mode} session started {fmtLocalDT(session.started_at)}. Equipment charges for the affected days will be re-computed. Continue?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={saving} data-testid="delete-session-confirm">
            {saving ? "Deleting…" : "Delete session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
